export const logger = (type: 'log' | 'error' | 'warn' = 'log', ...args: any) => {
    if(!(window as any).salla?.logger){
        return console[type]('%cSalla-Accounts', 'font-weight:bold; border:1px solid #bbf3e5; padding: 2px 6px; border-radius: 5px; color:#bbf3e5', ...args);
    }
    return (window as any).salla?.logger[type](...args);
}
export const log = (...args: any) => {
    return logger('log', ...args);
};

export const error = (...args: any) => {
    return logger('error', ...args);
};

export const warn = (...args: any) => {
    return logger('warn', ...args);
};

export const parentMessage = (message: any, type: 'ready' | 'height' | 'authenticated_web_auth' | 'authenticated' | 'notify' | 'dispatchEvent' | 'storage' | 'reload' | 'logged-out' | string) => {
    if(!inIframe()){
        return Salla.event.emit(`login::${type}`, message);
    }
    type = `iframe::${type}`;
    log('parent.postMessage', type, message);
    return window.parent.postMessage({
        source: "login",
        message: message,
        type: type
    }, "*");
}

export function inIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

/**
 * Custom URL scheme the native app registers to receive the social-login result.
 * Must stay in sync with the value the iOS/Android app registers (CFBundleURLSchemes
 * on iOS, the intent-filter scheme on Android).
 */
const MOBILE_SOCIAL_CALLBACK_SCHEME = 'sallaAccounts';

/**
 * Delivers a social-login result from a provider callback page.
 *
 * On iOS (ASWebAuthenticationSession) and Android (Chrome Custom Tabs) the flow runs
 * in an isolated browser session with no `window.opener` and no bridge back into the
 * app, so `postMessage`/`Salla.event` can't deliver the result. We detect that case
 * via the absence of `window.opener` and instead redirect to the registered custom
 * scheme, carrying the result in the query string — that redirect ends the session
 * and hands the URL back to the app.
 *
 * In the web popup flow `window.opener` exists, so we keep the existing behaviour
 * (emit the Salla event + postMessage to the opener, then close the popup).
 */
export const deliverSocialResult = (
  type: string,
  data: Record<string, any>,
  closeDelay: number = 1000
) => {
  const message = { type, ...data };

  // Native session (ASWebAuthenticationSession / Custom Tabs): no opener window, so
  // deliver via the custom scheme. A URL only carries strings, so we send the exact
  // same object as one JSON `payload` param — the app parses it and re-injects it
  // unchanged, keeping web and mobile perfectly in sync.
  // Use encodeURIComponent (spaces -> %20) rather than URLSearchParams (spaces -> +),
  // so any native URL parser (iOS URLComponents / Android Uri) decodes it correctly
  // without the "+ means space" form-encoding ambiguity.
  if (!window.opener) {
    const payload = encodeURIComponent(JSON.stringify(message));
    window.location.replace(`${MOBILE_SOCIAL_CALLBACK_SCHEME}://social-callback?payload=${payload}`);
    return;
  }

  // Web popup: hand the object to the opener and close.
  Salla.event.emit('auth::social-media-login', message);
  window.opener.postMessage(message, '*');
  setTimeout(() => window.close(), closeDelay);
};

const LOCAL_HOST = /(^|\.)salla\.test$/;
const BETA_HOST = /(^|\.)salla\.group$/;
const PROD_HOST = /(^|\.)salla\.(sa|dev|com|network)$/;

// `origin` is the embedder store's browser-set origin (an attacker can't forge it, and
// only Salla owns *.salla.test / *.salla.group), so a non-prod store may reach its
// matching non-prod backend instead of being forced onto production.
export function isTrustedApiUrl(url?: string, origin?: string): boolean {
  if (!url) return false;
  try {
    const { protocol, hostname } = new URL(url);
    let originHost = '';
    try { originHost = origin ? new URL(origin).hostname : ''; } catch {}
    // Local (salla.test) may use http; beta (*.salla.group) must stay https.
    if (LOCAL_HOST.test(originHost) && LOCAL_HOST.test(hostname)) return true;
    if (BETA_HOST.test(originHost) && protocol === 'https:' && BETA_HOST.test(hostname)) return true;
    return protocol === 'https:' && PROD_HOST.test(hostname);
  } catch (e) {
    return false;
  }
}


export async function waitForCondition(
  callback: () => boolean,
  timeout = 10000,
  interval = 50
) {
  const start = Date.now();

  while (Date.now() - start < timeout) {
    if (callback()) {
      return true;
    }
    await new Promise((resolve) => setTimeout(resolve, interval));
  }

  return false;
}

type TurnstileContext = {
  type?: 'mobile' | 'email' | string;
  method?: 'mobile' | 'email' | string;
  phone?: string;
  country_code?: string;
  email?: string;
};

export async function waitForTurnstile(
  widgetId: string = "#cloudflare-turnstile-widget",
  context: TurnstileContext = {}
) {
    //to make sure that the header is removed, to avoid doublicated key
    salla.api.setHeader( 's-render-key', null );

    const stage = { current: 'script_load' as 'script_load' | 'render' | 'token' };

    let isPassed = await waitForCondition(() => !!window.turnstile);

    if (isPassed) {
      stage.current = 'render';
      window.turnstile.remove(widgetId);
      isPassed = !!window.turnstile.render(widgetId);
    }

    if (isPassed) {
      stage.current = 'token';
      isPassed = await waitForCondition(() => !!salla.api.getHeader('s-render-key'), 20000);
    }

    if (!isPassed) {
       Salla.analytics.log("Login Error::waitForTurnstile timeout", {
         error_type: "re-captcha-v3::timeout",
         failed_stage: stage.current,
         widget_id: widgetId,
         type: context.type,
         method: context.method,
         phone: context.phone,
         country_code: context.country_code,
         email: context.email,
       });
       Salla.analytics.track('Authentication Failure', {
         type: context.type,
         method: context.method,
         phone: context.phone,
         country_code: context.country_code,
         email: context.email,
         failure_reason: 'turnstile_failed',
         failed_stage: stage.current,
         error: 're-captcha-v3::timeout',
       });
    }
    return isPassed;
  }

export function getTheme(): string {
  return document.documentElement.classList.contains("dark") ? 'dark' : 'light';
}

export const toggleDarkMode = (isDark: boolean) =>
  isDark
    ? document.documentElement.classList.add('dark')
    : document.documentElement.classList.remove('dark');
